import { useMemo, useRef } from 'react';

import useSWR, { useSWRConfig } from 'swr';

import { useCurrentProject } from '@adsk/alloy-provider';

import { getThumbnailURL, hasThumbnail } from '../api/attachments';
import { useAttachmentsContext } from '../context/AttachmentsContext';

type UseThumbnailReturnValue = {
  error: Error | undefined;
  thumbnail: string | undefined;
  loading: boolean;
};

type UseThumbnailProps = {
  attachmentId?: string;
};

const MAX_THUMBNAIL_RETRIES = 5;

const useThumbnail = ({
  attachmentId,
}: UseThumbnailProps): UseThumbnailReturnValue => {
  const retries = useRef(MAX_THUMBNAIL_RETRIES);
  const { attachments } = useAttachmentsContext();
  const { cache } = useSWRConfig();
  const scopes = useCurrentProject()?.scopes?.project;

  const attachment = useMemo(
    () =>
      attachments.find(
        (attachment) => attachment.attachmentId === attachmentId,
      ),
    [attachments, attachmentId],
  );

  const shouldHaveThumbnail = useMemo(
    () => (attachment ? hasThumbnail(attachment) : false),
    [attachment],
  );

  const swrKey = useMemo(() => `${attachmentId}_thumbnail`, [attachmentId]);
  const shouldRevalidate = useMemo(
    () => !cache.get(swrKey) && shouldHaveThumbnail,
    [cache, swrKey, shouldHaveThumbnail],
  );

  const { data: thumbnail, error } = useSWR(
    swrKey,
    () => {
      if (attachment && attachment.file) {
        return Promise.resolve(URL.createObjectURL(attachment.file));
      }

      if (attachment && shouldHaveThumbnail) {
        return getThumbnailURL(attachment, scopes);
      }

      return Promise.resolve(undefined);
    },
    {
      revalidateOnMount: shouldRevalidate,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      refreshInterval: (currentThumbnail) => {
        if (shouldHaveThumbnail && !currentThumbnail && retries.current > 0) {
          retries.current -= 1;
          return 5000;
        }

        return 0;
      },
    },
  );

  return {
    error,
    thumbnail,
    loading: !thumbnail && shouldHaveThumbnail,
  };
};

export default useThumbnail;
